import { getInputProps } from "@/utils/helpers/columnsHelper";
import { Box, Stack } from "@mui/material";
import {
  TextInput,
  SelectInput,
  DateInput,
  NumberInput,
  ReferenceInput,
  AutocompleteInput,
  useGetOne,
} from "react-admin";
import { useWatch, useFormContext } from "react-hook-form";
import { FormSection } from "@/components/common/inputs/FormSection";
import { parseDateInput, formatDateDisplay } from "@/resources/policies/components/PolicyCreateInputs";
import { Claimant, ResourceName, StoplossClaim, CLAIM_TYPE_CHOICES, CLAIM_STATUS_CHOICES } from "@/types";
import { useEffect } from "react";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

const getClaimItemInputProps = (key: string, textReplace = {}) =>
  getInputProps(key, textReplace, ResourceName.CLAIM_ITEMS);

export const ClaimItemCreateInputs = () => {
  const { setValue } = useFormContext();
  const claimantId = useWatch({ name: "claimant_id_ref" });

  // Fetch claimant data when selected
  const { data: claimant } = useGetOne<Claimant>(ResourceName.CLAIMANTS, { id: claimantId }, { enabled: !!claimantId });

  // Auto-populate client when claimant is selected
  useEffect(() => {
    if (claimant?.client_id_ref) {
      setValue("client_id_ref", claimant.client_id_ref);
    }
  }, [claimant, setValue]);

  return (
    <Box p={2}>
      <FormSection title="Essential Information">
        {/* Step 1: Select Claimant First */}
        <Box sx={{ mb: 2 }}>
          <ReferenceInput source="claimant_id_ref" reference={ResourceName.CLAIMANTS} required>
            <AutocompleteInput
              label="Claimant"
              optionText={(record: Claimant) => (record ? `${record.claimant_name}` : "")}
              fullWidth
              size="small"
              isRequired
            />
          </ReferenceInput>
        </Box>

        {/* Step 2: Show Stop-Loss Claim and Client after Claimant is selected */}
        {claimantId && (
          <>
            <Box sx={{ mb: 2 }}>
              <ReferenceInput
                source="stoploss_claim_id_ref"
                reference={ResourceName.STOPLOSS_CLAIMS}
                filter={{ claimant_id_ref: claimantId }}
              >
                <AutocompleteInput
                  label="Stop-Loss Claim"
                  optionText={(record: StoplossClaim) => {
                    if (!record) return "";
                    const type = record.stoploss_claim_type ? formatToTitleCase(record.stoploss_claim_type) : "";
                    const amount = record.total_paid_amount ? formatUSD(record.total_paid_amount as number) : "$0";
                    const status = record.claim_status ? formatToTitleCase(record.claim_status) : "";
                    return [type, amount, status].filter(Boolean).join(" · ");
                  }}
                  fullWidth
                  size="small"
                />
              </ReferenceInput>
            </Box>
            <Box sx={{ mb: 2 }}>
              <ReferenceInput source="client_id_ref" reference={ResourceName.CLIENTS}>
                <AutocompleteInput label="Client" optionText="client_legal_name" fullWidth size="small" disabled />
              </ReferenceInput>
            </Box>
          </>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <SelectInput
            {...getClaimItemInputProps("claim_type")}
            choices={CLAIM_TYPE_CHOICES}
            fullWidth
            size="small"
            isRequired
          />
          <SelectInput
            {...getClaimItemInputProps("claim_status")}
            choices={CLAIM_STATUS_CHOICES}
            fullWidth
            size="small"
            isRequired
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextInput {...getClaimItemInputProps("source_claim_id")} fullWidth size="small" isRequired />
          <TextInput {...getClaimItemInputProps("primary_diagnosis")} fullWidth size="small" isRequired />
          <TextInput {...getClaimItemInputProps("procedure_code")} fullWidth size="small" isRequired />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <DateInput
            {...getClaimItemInputProps("date_of_service")}
            parse={parseDateInput}
            format={formatDateDisplay}
            fullWidth
            size="small"
          />
          <DateInput
            {...getClaimItemInputProps("paid_date")}
            parse={parseDateInput}
            format={formatDateDisplay}
            fullWidth
            size="small"
          />
          <NumberInput {...getClaimItemInputProps("paid_amount")} step={0.01} min={0} fullWidth size="small" />
        </Stack>
      </FormSection>

      {/* Additional Fields */}
      <FormSection title="Additional Fields">
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <DateInput
            {...getClaimItemInputProps("date_of_service_start")}
            parse={parseDateInput}
            format={formatDateDisplay}
            fullWidth
            size="small"
          />
          <DateInput
            {...getClaimItemInputProps("date_of_service_end")}
            parse={parseDateInput}
            format={formatDateDisplay}
            fullWidth
            size="small"
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <NumberInput {...getClaimItemInputProps("billed_amount")} step={0.01} min={0} fullWidth size="small" />
          <NumberInput {...getClaimItemInputProps("eligible_amount")} step={0.01} min={0} fullWidth size="small" />
          <NumberInput {...getClaimItemInputProps("member_oop")} step={0.01} min={0} fullWidth size="small" />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <NumberInput {...getClaimItemInputProps("deductible_amount")} step={0.01} min={0} fullWidth size="small" />
          <NumberInput {...getClaimItemInputProps("coinsurance_amount")} step={0.01} min={0} fullWidth size="small" />
          <NumberInput {...getClaimItemInputProps("copay_amount")} step={0.01} min={0} fullWidth size="small" />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextInput {...getClaimItemInputProps("provider")} fullWidth size="small" />
          <TextInput {...getClaimItemInputProps("provider_type")} fullWidth size="small" />
        </Stack>

        <TextInput {...getClaimItemInputProps("procedure_description")} fullWidth size="small" multiline rows={2} />
      </FormSection>
    </Box>
  );
};
